import { useState, useMemo, useCallback } from "react";
import { Plus, Edit2, Trash } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
// import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { ProColumns } from "@ant-design/pro-components";
import { useQueryClient } from "@tanstack/react-query";
import { Popconfirm } from "antd";
import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import { TenantRoleService } from "@/services/APIs";
import { Role } from "@/utils/types/tenantPermissions";
import RoleModal from "./components/RoleModal";
import ConfigDynamicTable from "@/components/BaseTable/ConfigDynamicTable";

const RolesPermissionsPage = () => {
  const queryClient = useQueryClient();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [activeTab] = useState<"roles" | "designation">("roles");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  // Fetch user roles to count users assigned to each role
  // const { data: userRolesData } = useQuery({
  //   queryKey: ["userRoles"],
  //   queryFn: async () => {
  //     const response = await TenantUserRoleService.listUserRoles(1, 1000);
  //     return response?.results ?? [];
  //   },
  // });

  // Calculate user counts per role
  // const userCountsByRole = useMemo(() => {
  //   const counts: Record<string, number> = {};
  //   if (userRolesData) {
  //     userRolesData.forEach((userRole: any) => {
  //       const roleId = userRole.role;
  //       if (roleId) {
  //         counts[roleId] = (counts[roleId] ?? 0) + 1;
  //       }
  //     });
  //   }
  //   return counts;
  // }, [userRolesData]);

  const handleAddRole = () => {
    setEditingRole(null);
    setModalOpen(true);
  };

  const handleEditRole = useCallback((role: Role) => {
    setEditingRole(role);
    setModalOpen(true);
  }, []);

  const handleDeleteRole = useCallback(
    async (id: string) => {
      try {
        await TenantRoleService.deleteRole(id);
        notify.success("Role deleted successfully");
        queryClient.invalidateQueries({ queryKey: ["moduleData", "Roles"] });
        queryClient.invalidateQueries({ queryKey: ["tenantRoles"] });
      } catch (error: any) {
        notify.error(error?.message ?? "Failed to delete role");
      }
    },
    [queryClient]
  );

  const handleModalSuccess = () => {
    setModalOpen(false);
    setEditingRole(null);
    queryClient.invalidateQueries({ queryKey: ["moduleData", "Roles"] });
    queryClient.invalidateQueries({ queryKey: ["tenantRoles"] });
  };

  const handleModalCancel = () => {
    setModalOpen(false);
    setEditingRole(null);
  };

  // Define columns for the roles table
  const columns: ProColumns<Role>[] = useMemo(
    () => [
      {
        title: "Role Name",
        dataIndex: "name",
        key: "name",
        render: (_: any, record: Role) => (
          <span className="font-semibold text-[#111827]">{record.name}</span>
        ),
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        render: (_: any, record: Role) => (
          <span className="text-[#627384]">{record.description ?? "-"}</span>
        ),
      },
      // {
      //   title: "Users Assigned",
      //   key: "users_assigned",
      //   render: (_: any, record: Role) => {
      //     const userCount = userCountsByRole[record.id] ?? 0;
      //     return (
      //       <span
      //         className="inline-flex items-center rounded-full px-3 py-1 text-sm"
      //         style={{
      //           backgroundColor: "#E0F2FE",
      //           color: "#0369A1",
      //         }}
      //       >
      //         {userCount} {userCount === 1 ? "user" : "users"}
      //       </span>
      //     );
      //   },
      // },
      {
        title: "Actions",
        width: 120,
        valueType: "option",
        key: "actions",
        fixed: "right",
        render: (_: any, record: Role) => {
          return [
            <CustomButton
              key="edit"
              icon={<Edit2 size={16} />}
              type="text"
              onClick={() => handleEditRole(record)}
              title="Edit Role"
            />,
            <Popconfirm
              key="delete"
              title="Are you sure you want to delete this role?"
              onConfirm={() => handleDeleteRole(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <CustomButton
                danger
                icon={<Trash size={16} />}
                type="text"
                title="Delete Role"
              />
            </Popconfirm>,
          ];
        },
      },
    ],
    [handleDeleteRole, handleEditRole]
  );

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      {/* Page Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
            Roles & Permissions
          </h1>
          <p className="text-sm text-[#627384]">
            Manage user roles and designations
          </p>
        </div>
        {activeTab === "roles" && (
          <Button
            onClick={handleAddRole}
            className="bg-primary hover:bg-primary/90 flex items-center gap-2 whitespace-nowrap text-white"
          >
            <Plus className="h-4 w-4" />
            Add Role
          </Button>
        )}
      </div>

      {/* Tabs */}
      {/* <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "roles" | "designation")}>
        <TabsList className="bg-white">
          <TabsTrigger
            value="roles"
            className={`flex items-center gap-2 ${
              activeTab === "roles"
                ? "bg-[#E0F2FE] text-[#111827]"
                : "text-[#627384]"
            }`}
          >
            <Shield
              size={16}
              className={activeTab === "roles" ? "text-[#111827]" : "text-[#627384]"}
            />
            Roles
          </TabsTrigger>
          <TabsTrigger
            value="designation"
            className={`flex items-center gap-2 ${
              activeTab === "designation"
                ? "bg-[#E0F2FE] text-[#111827]"
                : "text-[#627384]"
            }`}
          >
            <Building2
              size={16}
              className={
                activeTab === "designation" ? "text-[#111827]" : "text-[#627384]"
              }
            />
            Designation
          </TabsTrigger>
        </TabsList>
      </Tabs> */}

      {/* Content Card */}
      {activeTab === "roles" && (
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-3 md:p-4">
            {/* <div className="mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#111827]" />
              <h2 className="text-lg font-semibold text-[#111827]">
                Existing Roles
              </h2>
            </div> */}
            <ConfigDynamicTable<Role>
              tableName="Roles"
              columns={columns}
              headerTitle="Existing Roles"
              rowKey="id"
              toolBarRender={() => []}
              selectedRowKeys={selectedRowKeys}
              setSelectedRowKeys={setSelectedRowKeys}
              filter={{}}
            />
          </CardContent>
        </Card>
      )}

      {/* {activeTab === "designation" && (
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-4">
            <div className="mb-4 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#111827]" />
              <h2 className="text-lg font-semibold text-[#111827]">
                Designations
              </h2>
            </div>
            <p className="text-[#627384]">Designation management coming soon...</p>
          </CardContent>
        </Card>
      )} */}

      {/* Role Modal */}
      <RoleModal
        open={modalOpen}
        onCancel={handleModalCancel}
        onSuccess={handleModalSuccess}
        initialData={editingRole}
        isEditMode={!!editingRole}
      />
    </div>
  );
};

export default RolesPermissionsPage;
